import { Component, OnInit, OnDestroy, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { DevicesService } from '../../../services/requests/devices.service';
import { StrategiesService } from '../../../services/requests/strategies.service';
import { WebhooksService } from '../../../services/requests/webhooks.service';
import { ModalClickOutsideService } from '../../../services/modal-click-outside.service';

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-system-connection',
  templateUrl: './system-connection.component.html',
  styleUrls: ['./system-connection.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SystemConnectionComponent implements OnInit, OnDestroy, AfterViewInit {
  machines: any[] = [];
  filtered: any[] = [];
  userId: string | null = '';
  isAdmin = false;
  isLoading = false;
  isSaving = false;
  private savingCount = 0;
  private strategiesLoaded = false;
  private webhooksLoaded = false;
  strategies: any[] = [];
  webhooks: any[] = [];
  editingDeviceId: string | null = null;
  editingDeviceName: string = '';
  showEditModal = false;
  editingMachine: any = null;
  showPassword = false;
  showWebhookDropdown = false;
  
  showStrategyModal = false;
  viewingStrategy: any = null;
  sortField: string = 'priorite';
  sortDirection: 'asc' | 'desc' = 'asc';

  @ViewChild('editModalContent', { static: false }) editModalContent!: ElementRef;
  @ViewChild('strategyModalContent', { static: false }) strategyModalContent!: ElementRef;

  private _searchTerm: string = '';
  get searchTerm(): string {
    return this._searchTerm;
  }
  set searchTerm(value: string) {
    this._searchTerm = value;
    this.applyFilter();
  }

  constructor(
    private devicesService: DevicesService,
    private strategiesService: StrategiesService,
    private webhooksService: WebhooksService,
    private modalClickOutsideService: ModalClickOutsideService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userId = localStorage.getItem('idUser');
    this.isAdmin = localStorage.getItem('isAdmin') === 'true';

    const savedSort = localStorage.getItem('localStorage_SP-SystemConnection');
    if (savedSort) {
      try {
        const sortData = JSON.parse(savedSort);
        this.sortField = sortData.field || 'priorite';
        this.sortDirection = sortData.direction || 'asc';
      } catch (e) {
        console.error('Erreur lors de la récupération du tri system-connection :', e);
      }
    }

    this.isLoading = true;
    this.loadMachines();
    this.loadStrategies();
    this.loadWebhooks();
  }

  private documentClickHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('.webhook-dropdown-container')) {
      return;
    }
    this.showWebhookDropdown = false;
  };

  ngAfterViewInit(): void {
    this.updateModalRegistration();
    document.addEventListener('click', this.documentClickHandler);
  }

  private updateModalRegistration(): void {
    if (this.editModalContent) {
      if (this.showEditModal) {
        this.modalClickOutsideService.registerModal(this.editModalContent, () => this.closeEditModal());
      } else {
        this.modalClickOutsideService.unregisterModal(this.editModalContent);
      }
    }
    if (this.strategyModalContent) {
      if (this.showStrategyModal) {
        this.modalClickOutsideService.registerModal(this.strategyModalContent, () => this.closeStrategyModal());
      } else {
        this.modalClickOutsideService.unregisterModal(this.strategyModalContent);
      }
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    document.removeEventListener('click', this.documentClickHandler);
    if (this.editModalContent) {
      this.modalClickOutsideService.unregisterModal(this.editModalContent);
    }
    if (this.strategyModalContent) {
      this.modalClickOutsideService.unregisterModal(this.strategyModalContent);
    }
  }

  private updateScroll(): void {
    document.body.style.overflow = (this.showEditModal || this.showStrategyModal) ? 'hidden' : '';
  }

  getMachineNameParts(name: string): { main: string; parenthetical: string } {
    if (!name) return { main: name || '', parenthetical: '' };
    const match = name.match(/^(.+?)\s*\((.+?)\)$/);
    if (match) {
      return { main: match[1].trim(), parenthetical: `(${match[2]})` };
    }
    return { main: name, parenthetical: '' };
  }

  toggleSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    
    try {
      localStorage.setItem('localStorage_SP-SystemConnection', JSON.stringify({
        field: this.sortField,
        direction: this.sortDirection
      }));
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du tri system-connection :', e);
    }
    
    this.applyFilter();
  }

  applyFilter(): void {
    const term = this._searchTerm.toLowerCase().trim();
    let base = [...this.machines];

    if (term) {
      base = base.filter((m) => {
        const nameMatch = m.name?.toLowerCase().includes(term);
        let webhookMatch = false;
        if (m.webhookUrl) {
          if (typeof m.webhookUrl === 'object' && m.webhookUrl.nom) {
            webhookMatch = m.webhookUrl.nom.toLowerCase().includes(term);
          } else if (typeof m.webhookUrl === 'string') {
            const webhook = this.webhooks.find(w => w._id === m.webhookUrl);
            if (webhook) {
              webhookMatch = webhook.nom.toLowerCase().includes(term) || 
                           webhook.webhookUrlReceipt.toLowerCase().includes(term);
            }
          }
        }
        return nameMatch || webhookMatch;
      });
    }

    base.sort((a, b) => {
      let valueA: any;
      let valueB: any;
      
      switch (this.sortField) {
        case 'name':
          valueA = a.name?.toLowerCase() || '';
          valueB = b.name?.toLowerCase() || '';
          return this.sortDirection === 'asc' 
            ? valueA.localeCompare(valueB) 
            : valueB.localeCompare(valueA);
        
        case 'priorite':
          valueA = a.priorite || 0;
          valueB = b.priorite || 0;
          return this.sortDirection === 'asc' 
            ? valueA - valueB 
            : valueB - valueA;
        
        default:
          return 0;
      }
    });

    this.filtered = base;
  }

  private extractWebhookId(webhookUrl: any): string | null {
    if (!webhookUrl) return null;
    if (typeof webhookUrl === 'object' && webhookUrl._id) return webhookUrl._id;
    if (typeof webhookUrl === 'string') return webhookUrl;
    return null;
  }

  updateConnexion(machine: any): void {
    const {
      _id,
      ip,
      ipSecondaire,
      identifiant,
      passwordInput,
      originalIp,
      originalIpSecondaire,
      originalIdentifiant,
      selectedStrategie,
      webhookUrl,
      originalWebhookUrl,
      remunerationReceptionEnvoi,
      originalRemunerationReceptionEnvoi,
      remunerationReceptionSwitch,
      originalRemunerationReceptionSwitch
    } = machine;

    const webhookId = this.extractWebhookId(webhookUrl);
    const originalWebhookId = this.extractWebhookId(originalWebhookUrl);

    const updateData: any = {
      idMachine: _id,
      ...(ip !== originalIp && { ip }),
      ...(ipSecondaire !== originalIpSecondaire && { ipSecondaire }),
      ...(identifiant !== originalIdentifiant && { identifiant }),
      ...(webhookId !== originalWebhookId && { webhookUrl: webhookId }),
      ...(remunerationReceptionEnvoi !== originalRemunerationReceptionEnvoi && { remunerationReceptionEnvoi }),
      ...(remunerationReceptionSwitch !== originalRemunerationReceptionSwitch && { remunerationReceptionSwitch }),
      ...(selectedStrategie && { idStrat: selectedStrategie })
    };

    if (passwordInput !== undefined) {
      updateData.password = passwordInput;
    }

    const hasModifications = Object.keys(updateData).some((key) => key !== 'idMachine' && (key !== 'password' || passwordInput !== undefined));

    if (!hasModifications) {
      showNotif('Aucune modification détectée', 'info');
      return;
    }

    machine.updating = true;
    this.startSaving();

    this.devicesService.updateConnexion(updateData).subscribe({
      next: (response: any) => {
        machine.updating = false;
        this.stopSaving();
        if (response.status) {
          machine.originalIp = ip;
          machine.originalIpSecondaire = ipSecondaire;
          machine.originalIdentifiant = identifiant;
          machine.originalWebhookUrl = webhookUrl;
          machine.originalRemunerationReceptionEnvoi = remunerationReceptionEnvoi;
          machine.originalRemunerationReceptionSwitch = remunerationReceptionSwitch;
          machine.passwordInput = '';
          machine.updated = true;
          showNotif('✅ Connexion mise à jour avec succès', 'success');
          setTimeout(() => (machine.updated = false), 2000);
        } else {
          showNotif(response.message || '❌ Erreur lors de la mise à jour de la connexion', 'error');
        }
      },
      error: (error: any) => {
        machine.updating = false;
        this.stopSaving();
        console.error('❌ Erreur updateConnexion :', error);
        showNotif(error?.error?.message || '❌ Erreur serveur lors de la mise à jour', 'error');
      }
    });
  }

  updatePriorite(idMachine: any, event: any): void {
    const selectElement = event.target as HTMLSelectElement;
    const value = selectElement.value;

    this.devicesService.updateUsingFor({idMachine: idMachine, value: value}).subscribe({
      next: (response: any) => {
        if (response.status) {
          const machine = this.machines.find(m => m._id === idMachine);
          if (machine) {
            machine.usedFor = value;
          }
          showNotif('✅ Priorité mise à jour avec succès', 'success');
          this.applyFilter();
        } else {
          showNotif(response.message || '❌ Erreur lors du changement de priorité', 'error');
        }
      },
      error: (error: any) => {
        showNotif(error?.error?.message || '❌ Erreur lors du changement de priorité', 'error');
      }
    });
  }

  copy(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      showNotif('📋 Mot de passe copié', 'success');
    }).catch(() => {
      console.error('❌ Erreur lors de la copie');
      showNotif('❌ Erreur lors de la copie du mot de passe', 'error');
    });
  }

  copyWebhook(webhookUrl: any): void {
    const url = this.getWebhookUrl(webhookUrl);
    if (!url) {
      showNotif('❌ Aucune URL webhook disponible', 'error');
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      showNotif('📋 URL webhook copiée', 'success');
    }).catch(() => {
      console.error('❌ Erreur lors de la copie');
      showNotif('❌ Erreur lors de la copie de l\'URL webhook', 'error');
    });
  }

  copyFormattedIp(ip: string): void {
    const texte = ip?.trim();
    if (!texte) return;

    navigator.clipboard.writeText(texte).then(() => {
      showNotif('📋 IP copiée', 'success');
    }).catch(() => {
      console.error('❌ Erreur lors de la copie IP');
      showNotif('❌ Erreur lors de la copie de l\'IP', 'error');
    });
  }

  openDeviceUrl(machine: any): void {
    if (!machine.ip) {
      showNotif('❌ Aucune URL configurée pour cette machine', 'error');
      return;
    }

    let url = machine.ip;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'http://' + url;
    }

    window.open(url, '_blank');
  }

  togglePassword(machine: any): void {
    machine.showPassword = !machine.showPassword;
  }

  onEnterKey(event: Event, machine: any): void {
    event.preventDefault();
    this.updateConnexion(machine);
  }

  private startSaving(): void {
    this.savingCount++;
    this.isSaving = true;
  }

  private stopSaving(): void {
    this.savingCount = Math.max(0, this.savingCount - 1);
    if (this.savingCount === 0) {
      this.isSaving = false;
    }
  }

  private loadMachines(): void {
    const source$ = this.isAdmin
      ? this.devicesService.getAll()
      : this.devicesService.getByUser(this.userId || '');

    source$.subscribe({
      next: (response: any) => {
        const list = response?.data?.devices;
        if (response.status && Array.isArray(list)) {
          this.machines = list.map((device: any) => this.mapDevice(device));
          this.applyFilter();
        } else {
          showNotif(response.message || '❌ Erreur lors du chargement des machines', 'error');
        }
        this.checkLoadingComplete();
      },
      error: (error: any) => {
        console.error('❌ Erreur getDevices :', error);
        showNotif(error?.error?.message || '❌ Erreur serveur lors du chargement des machines', 'error');
        this.checkLoadingComplete();
      }
    });
  }

  private loadStrategies(): void {
    this.strategiesService.getAll().subscribe({
      next: (response: any) => {
        if (response.status) {
          this.strategies = response.data.strategies || [];
        } else {
          showNotif(response.message || '❌ Erreur lors du chargement des stratégies', 'error');
        }
        this.strategiesLoaded = true;
        this.checkLoadingComplete();
      },
      error: (error: any) => {
        console.error('❌ Erreur getStrategies :', error);
        showNotif(error?.error?.message || '❌ Erreur serveur lors du chargement des stratégies', 'error');
        this.strategiesLoaded = true;
        this.checkLoadingComplete();
      }
    });
  }

  private loadWebhooks(): void {
    this.webhooksService.getAll().subscribe({
      next: (response: any) => {
        if (response.status) {
          this.webhooks = response.data?.webhooks || [];
        }
        this.webhooksLoaded = true;
        this.checkLoadingComplete();
      },
      error: (error: any) => {
        console.error('❌ Erreur getWebhooks :', error);
        this.webhooksLoaded = true;
        this.checkLoadingComplete();
      }
    });
  }

  private checkLoadingComplete(): void {
    if (this.strategiesLoaded && this.webhooksLoaded) {
      this.isLoading = false;
    }
  }

  getWebhookName(webhookUrl: any): string {
    if (!webhookUrl) return '-';
    
    if (typeof webhookUrl === 'object') {
      if (webhookUrl.nom) {
        return webhookUrl.nom;
      }
      if (webhookUrl._id) {
        const webhook = this.webhooks.find(w => {
          const wId = w._id?.toString();
          const searchId = webhookUrl._id?.toString();
          return wId === searchId;
        });
        if (webhook && webhook.nom) {
          return webhook.nom;
        }
      }
    }
    
    if (typeof webhookUrl === 'string' && this.webhooks.length) {
      const webhookUrlStr = webhookUrl.toString();
      
      if (webhookUrlStr.startsWith('http://') || webhookUrlStr.startsWith('https://')) {
        const webhook = this.webhooks.find(w => w.webhookUrlReceipt === webhookUrlStr);
        if (webhook && webhook.nom) {
          return webhook.nom;
        }
      } else {
        const webhook = this.webhooks.find(w => {
          const webhookId = w._id?.toString();
          const searchId = webhookUrlStr;
          return webhookId === searchId;
        });
        if (webhook && webhook.nom) {
          return webhook.nom;
        }
      }
    }
    
    return '-';
  }

  getWebhookUrl(webhookUrl: any): string {
    if (!webhookUrl) {
      return '';
    }
    
    if (typeof webhookUrl === 'object') {
      if (webhookUrl.webhookUrlReceipt) {
        return webhookUrl.webhookUrlReceipt;
      }
      if (webhookUrl._id) {
        const webhook = this.webhooks.find(w => {
          const wId = w._id?.toString();
          const searchId = webhookUrl._id?.toString();
          return wId === searchId;
        });
        if (webhook && webhook.webhookUrlReceipt) {
          return webhook.webhookUrlReceipt;
        }
      }
    }
    
    if (typeof webhookUrl === 'string') {
      const webhookUrlStr = webhookUrl.toString();
      
      if (webhookUrlStr.startsWith('http://') || webhookUrlStr.startsWith('https://')) {
        return webhookUrlStr;
      }
      
      if (this.webhooks.length === 0) {
        return '';
      }
      
      const webhook = this.webhooks.find(w => {
        const webhookId = w._id?.toString();
        const searchId = webhookUrlStr;
        return webhookId === searchId;
      });
      if (webhook && webhook.webhookUrlReceipt) {
        return webhook.webhookUrlReceipt;
      }
      
      const webhookByUrl = this.webhooks.find(w => w.webhookUrlReceipt === webhookUrlStr);
      if (webhookByUrl && webhookByUrl.webhookUrlReceipt) {
        return webhookByUrl.webhookUrlReceipt;
      }
    }
    
    return '';
  }

  toggleWebhookSelection(webhookId: string): void {
    if (!this.editingMachine?.selectedWebhookIds) return;
    const index = this.editingMachine.selectedWebhookIds.indexOf(webhookId);
    index > -1 
      ? this.editingMachine.selectedWebhookIds.splice(index, 1)
      : this.editingMachine.selectedWebhookIds.push(webhookId);
  }

  clearAllWebhooks(): void {
    if (this.editingMachine) this.editingMachine.selectedWebhookIds = [];
  }

  toggleWebhookDropdown(): void {
    this.showWebhookDropdown = !this.showWebhookDropdown;
  }

  getSelectedWebhooksDisplay(): string {
    if (!this.editingMachine?.selectedWebhookIds?.length) return '';
    
    if (this.editingMachine.selectedWebhookIds.length === 1 && this.editingMachine.originalWebhookUrl) {
      const name = this.getWebhookName(this.editingMachine.originalWebhookUrl);
      if (name && name !== '-') return name;
    }
    
    const names = this.editingMachine.selectedWebhookIds
      .map((id: string) => this.webhooks.find(w => w._id?.toString() === id?.toString())?.nom)
      .filter(Boolean) as string[];
    
    if (!names.length) return '';
    if (names.length === 1) return names[0];
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} autre${names.length > 3 ? 's' : ''}`;
  }

  startEditingDeviceName(machine: any, event: Event): void {
    event.stopPropagation();
    this.editingDeviceId = machine._id;
    this.editingDeviceName = machine.name;
  }

  cancelEditingDeviceName(): void {
    this.editingDeviceId = null;
    this.editingDeviceName = '';
  }

  saveDeviceName(machine: any): void {
    const newName = this.editingDeviceName.trim();
    
    if (!newName) {
      showNotif('❌ Le nom ne peut pas être vide', 'error');
      return;
    }
    
    if (newName === machine.name) {
      this.cancelEditingDeviceName();
      return;
    }

    this.devicesService.updateName({ idMachine: machine._id, name: newName }).subscribe({
      next: (response: any) => {
        if (response.status) {
          machine.name = newName;
          showNotif('✅ Nom de la machine mis à jour avec succès', 'success');
          this.cancelEditingDeviceName();
          this.applyFilter();
        } else {
          showNotif(response.message || '❌ Erreur lors de la mise à jour du nom', 'error');
        }
      },
      error: (error: any) => {
        console.error('❌ Erreur updateDeviceName:', error);
        showNotif(error?.error?.message || '❌ Erreur serveur lors de la mise à jour du nom', 'error');
      }
    });
  }

  openEditModal(machine: any): void {
    const webhookId = this.extractWebhookId(machine.webhookUrl);
    const selectedWebhookIds = webhookId ? [webhookId] : [];
    
    this.editingMachine = {
      _id: machine._id,
      name: machine.name || '',
      ip: machine.ip || '',
      ipSecondaire: machine.ipSecondaire || '',
      identifiant: machine.identifiant || '',
      passwordInput: machine.passwordInput || '',
      selectedWebhookIds,
      webhookUrl: machine.webhookUrl || null,
      originalWebhookUrl: machine.webhookUrl || null,
      usedForReception: machine.usedForReception || false,
      selectedStrategie: machine.selectedStrategie || null,
      remunerationReceptionEnvoi: machine.remunerationReceptionEnvoi || 0,
      remunerationReceptionSwitch: machine.remunerationReceptionSwitch || 0,
      originalIp: machine.originalIp || machine.ip || '',
      originalIpSecondaire: machine.originalIpSecondaire || machine.ipSecondaire || '',
      originalIdentifiant: machine.originalIdentifiant || machine.identifiant || '',
      originalRemunerationReceptionEnvoi: machine.originalRemunerationReceptionEnvoi || machine.remunerationReceptionEnvoi || 0,
      originalRemunerationReceptionSwitch: machine.originalRemunerationReceptionSwitch || machine.remunerationReceptionSwitch || 0,
      originalUsedForReception: machine.originalUsedForReception !== undefined ? machine.originalUsedForReception : (machine.usedForReception || false),
      originalSelectedWebhookIds: selectedWebhookIds
    };
    this.showPassword = false;
    this.showEditModal = true;
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistration();
    }, 0);
  }

  closeEditModal(): void {
    if (!this.isSaving) {
      this.showEditModal = false;
      this.editingMachine = null;
      this.showPassword = false;
      this.showWebhookDropdown = false;
      this.updateScroll();
      this.updateModalRegistration();
    }
  }

  saveFromModal(): void {
    if (!this.editingMachine) return;

    const machine = this.machines.find(m => m._id === this.editingMachine._id);
    if (!machine) return;

    const hasNameChange = machine.name !== this.editingMachine.name;
    const hasUsedForReceptionChange = (machine.usedForReception !== undefined ? machine.usedForReception : false) !== (this.editingMachine.usedForReception !== undefined ? this.editingMachine.usedForReception : false);
    const selectedWebhookId = (this.editingMachine.selectedWebhookIds || [])[0] || null;
    const webhookUrlToSave = selectedWebhookId || null;
    const hasWebhookChange = JSON.stringify((this.editingMachine.originalSelectedWebhookIds || []).sort()) !== 
                             JSON.stringify((this.editingMachine.selectedWebhookIds || []).sort());

    const updateData: any = {
      idMachine: this.editingMachine._id,
      ...(this.editingMachine.ip !== this.editingMachine.originalIp && { ip: this.editingMachine.ip }),
      ...(this.editingMachine.ipSecondaire !== this.editingMachine.originalIpSecondaire && { ipSecondaire: this.editingMachine.ipSecondaire }),
      ...(this.editingMachine.identifiant !== this.editingMachine.originalIdentifiant && { identifiant: this.editingMachine.identifiant }),
      ...(hasWebhookChange && { webhookUrl: webhookUrlToSave }),
      ...(this.editingMachine.remunerationReceptionEnvoi !== this.editingMachine.originalRemunerationReceptionEnvoi && { remunerationReceptionEnvoi: this.editingMachine.remunerationReceptionEnvoi }),
      ...(this.editingMachine.remunerationReceptionSwitch !== this.editingMachine.originalRemunerationReceptionSwitch && { remunerationReceptionSwitch: this.editingMachine.remunerationReceptionSwitch }),
      ...(this.editingMachine.selectedStrategie && { idStrat: this.editingMachine.selectedStrategie }),
      ...(hasUsedForReceptionChange && { usedForReception: this.editingMachine.usedForReception || false })
    };

    if (this.editingMachine.passwordInput && this.editingMachine.passwordInput.trim() !== '') {
      updateData.password = this.editingMachine.passwordInput;
    }

    const hasConnexionModifications = Object.keys(updateData).some((key) => key !== 'idMachine' && (key !== 'password' || this.editingMachine.passwordInput) && key !== 'usedForReception');

    if (!hasConnexionModifications && !hasNameChange && !hasUsedForReceptionChange && !hasWebhookChange) {
      showNotif('Aucune modification détectée', 'info');
      return;
    }

    this.isSaving = true;
    let completed = 0;
    const total = (hasConnexionModifications ? 1 : 0) + (hasNameChange ? 1 : 0) + (hasUsedForReceptionChange ? 1 : 0);

    const updateComplete = () => {
      completed++;
      if (completed === total) {
        this.isSaving = false;
        machine.name = this.editingMachine.name;
        machine.ip = this.editingMachine.ip;
        machine.originalIp = this.editingMachine.ip;
        machine.ipSecondaire = this.editingMachine.ipSecondaire;
        machine.originalIpSecondaire = this.editingMachine.ipSecondaire;
        machine.identifiant = this.editingMachine.identifiant;
        machine.originalIdentifiant = this.editingMachine.identifiant;
        if (hasWebhookChange) {
          machine.webhookUrl = webhookUrlToSave;
          machine.originalWebhookUrl = webhookUrlToSave;
          machine.selectedWebhookIds = this.editingMachine.selectedWebhookIds;
        }
        machine.remunerationReceptionEnvoi = this.editingMachine.remunerationReceptionEnvoi;
        machine.originalRemunerationReceptionEnvoi = this.editingMachine.remunerationReceptionEnvoi;
        machine.remunerationReceptionSwitch = this.editingMachine.remunerationReceptionSwitch;
        machine.originalRemunerationReceptionSwitch = this.editingMachine.remunerationReceptionSwitch;
        machine.selectedStrategie = this.editingMachine.selectedStrategie;
        machine.usedForReception = this.editingMachine.usedForReception;
        machine.originalUsedForReception = this.editingMachine.usedForReception;
        machine.passwordInput = '';
        showNotif('✅ Connexion mise à jour avec succès', 'success');
        this.closeEditModal();
        this.applyFilter();
      }
    };

    if (hasNameChange) {
      this.devicesService.updateName({ idMachine: this.editingMachine._id, name: this.editingMachine.name }).subscribe({
        next: (response: any) => {
          if (response.status) {
            updateComplete();
          } else {
            this.isSaving = false;
            showNotif(response.message || '❌ Erreur lors de la mise à jour du nom', 'error');
          }
        },
        error: (error: any) => {
          this.isSaving = false;
          showNotif(error?.error?.message || '❌ Erreur lors de la mise à jour du nom', 'error');
        }
      });
    }

    if (hasConnexionModifications || hasWebhookChange) {
      this.devicesService.updateConnexion(updateData).subscribe({
        next: (response: any) => {
          if (response.status) {
            updateComplete();
          } else {
            this.isSaving = false;
            showNotif(response.message || '❌ Erreur lors de la mise à jour de la connexion', 'error');
          }
        },
        error: (error: any) => {
          this.isSaving = false;
          console.error('❌ Erreur updateConnexion :', error);
          showNotif(error?.error?.message || '❌ Erreur serveur lors de la mise à jour', 'error');
        }
      });
    }
  }

  getStrategyName(strategyId: string | null): string {
    if (!strategyId) return '';
    const strategy = this.strategies.find(s => s._id === strategyId);
    return strategy ? `${strategy.operateur} (${strategy.typeStrategie})` : '';
  }

  getStrategy(strategyId: string | null): any {
    if (!strategyId) return null;
    return this.strategies.find(s => s._id === strategyId) || null;
  }

  openStrategyModal(strategy: any): void {
    if (!strategy) {
      return;
    }
    this.viewingStrategy = { ...strategy };
    this.showStrategyModal = true;
    this.cdr.detectChanges();
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistration();
    }, 0);
  }

  closeStrategyModal(): void {
    this.showStrategyModal = false;
    this.viewingStrategy = null;
    this.updateScroll();
    this.updateModalRegistration();
  }

  private mapDevice(device: any): any {
    const webhookUrl = device.webhookUrl || null;
    
    return {
      _id: device._id,
      name: device.name,
      ip: device.ip || '',
      ipSecondaire: device.ipSecondaire || '',
      identifiant: device.identifiant || '',
      passwordInput: device.password || '',
      originalIp: device.ip || '',
      originalIpSecondaire: device.ipSecondaire || '',
      originalIdentifiant: device.identifiant || '',
      usedForReception: device.usedForReception !== undefined ? device.usedForReception : false,
      originalUsedForReception: device.usedForReception !== undefined ? device.usedForReception : false,
      priorite: device.priorite || 0,
      webhookUrl: webhookUrl,
      originalWebhookUrl: webhookUrl,
      remunerationReceptionEnvoi: device.remunerationReceptionEnvoi,
      originalRemunerationReceptionEnvoi: device.remunerationReceptionEnvoi,
      remunerationReceptionSwitch: device.remunerationReceptionSwitch,
      originalRemunerationReceptionSwitch: device.remunerationReceptionSwitch,
      selectedStrategie: device.idStrat || null,
      responseUrl: device.responseUrl || '',
      isOnline: device.isOnline !== undefined ? device.isOnline : false,
      showPassword: false,
      updated: false,
      updating: false
    };
  }
}
